import { DateTime } from "luxon";
import type React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../hooks/useMobile";
import { trackErrorEvent, trackEvent } from "../utils/analytics";
import { LeveledError } from "../utils/errors";
import type { MarkNotificationClickedRequest, MarkNotificationsSeenRequest, NotificationResponse, ShiftNotificationDetails } from "../utils/models";
import axiosInstance from "../utils/networkWrapper";
import { visibleNotificationsForViewport } from "../utils/notificationVisibility";
import { isHeadNurse } from "../utils/utilFunctions";
import { useAuth } from "./AuthProvider";
import { type HospitalDataContextType, useHospitalData } from "./HospitalDataProvider";
import { useSnackbar } from "./SnackbarContext";

type NotificationsContextType = {
	allNotifications: NotificationResponse[];
	unseenNotificationCount: number;
	markNotificationsSeen: (notificationIds: number[]) => Promise<boolean>;
	markNotificationClicked: (notificationId: number) => Promise<boolean>;
	isLoading: boolean;
	fetchNotifications: () => Promise<void>;
};

const NotificationsContext = createContext<NotificationsContextType | null>(null);

const injectBirthdayNotification = (notifications: NotificationResponse[]): NotificationResponse[] => {
	// Example function to inject a birthday notification for demo purposes
	const birthdayNotification: NotificationResponse = {
		clicked_at: null,
		department_id: "",
		expires_at: null,
		notification_data: undefined,
		notification_email_sent: false,
		notification_email_sent_timestamp: null,
		notification_sms_sent: false,
		notification_sms_sent_timestamp: null,
		nurse_id: null,
		priority: 0,
		reason: null,
		request_id: "",
		seen_at: null,
		id: 1234,
		notification_type: "birthday",
		title: "Jennifer's Birthday is coming up!",
		message: `Their birthday is ${DateTime.now().plus({ days: 1 }).toFormat("MMM dd")}.\nThey’re on the morning shift tomorrow. Don’t forget to wish them Happy Birthday! 🎂`,
		created_at: DateTime.now().minus({ day: 2 }),
		isSeen: false,
		isClicked: false,
		user_id: "user_id_demo",
	};
	return [birthdayNotification, ...notifications];
};

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { t } = useTranslation();
	const [fetchedNotifications, setFetchedNotifications] = useState<NotificationResponse[]>([]);
	// `loadingRef` is the synchronous re-entrancy guard (dedupes overlapping fetches); `isLoading`
	// is the reactive signal consumers render on. Both are needed — a ref alone never re-renders.
	const loadingRef = useRef(false);
	const [isLoading, setIsLoading] = useState(false);
	const { userInfo } = useAuth();
	const { showSnackbar } = useSnackbar();
	const { isMobile } = useIsMobile();
	const { chosenDepartment, getWeekIndexByDate, getDayIndexByDate, isAnyDemo }: HospitalDataContextType = useHospitalData();

	// Both the list and the bell's badge derive from the same viewport-filtered array, so a hidden
	// notification can never inflate the unseen count (and a resize re-derives without a refetch).
	const allNotifications = useMemo(() => visibleNotificationsForViewport(fetchedNotifications, isMobile), [fetchedNotifications, isMobile]);
	const unseenNotificationCount = useMemo(() => allNotifications.filter((notification) => !notification.isSeen).length, [allNotifications]);

	const fetchNotifications = useCallback(async () => {
		if (!userInfo || !userInfo.id || chosenDepartment.department_id === "") {
			setFetchedNotifications([]);
			// Don't clear the flag mid-request; the in-flight fetch's `finally` resets it.
			if (!loadingRef.current) setIsLoading(false);
			return;
		}

		if (loadingRef.current) return;

		loadingRef.current = true;
		setIsLoading(true);

		try {
			const params = isHeadNurse(userInfo.role) ? { department_id: chosenDepartment.department_id } : {};
			const response = await axiosInstance.get<unknown[]>("/staffing/notifications", { params });

			const rawNotificationsArr = response.data
				.map<NotificationResponse | null>((notification_response: any) => {
					try {
						let parsedData: Partial<ShiftNotificationDetails>;

						// Handle both string and object formats
						if (typeof notification_response.notification_data === "string") {
							parsedData = JSON.parse(notification_response.notification_data) as ShiftNotificationDetails;
						} else if (typeof notification_response.notification_data === "object" && notification_response.notification_data !== null) {
							parsedData = notification_response.notification_data as ShiftNotificationDetails;
						} else {
							throw new Error("Invalid notification_data format");
						}

						// ignore admin notification and admin notification action required
						if (parsedData.notification_type === "admin_notification" || parsedData.notification_type === "admin_notification_action_required") {
							return null;
						}

						const noShiftDateTypes = [
							"nurse_termination",
							"performance_report",
							"daily_pickup_summary",
							"engagement",
							"schedule_published",
							"employee_status_decision",
						];
						if (!noShiftDateTypes.includes(notification_response.notification_type) && !parsedData.shift_date) {
							throw new LeveledError("Missing shift_date in notification data", "warning");
						}

						if (parsedData.shift_date) {
							const date = parsedData.shift_date;
							if (!date.isValid) {
								throw new Error("Invalid shift_date format");
							}

							parsedData.shift_date = date;
							parsedData.weekIndex = getWeekIndexByDate(date);
							parsedData.dayNo = getDayIndexByDate(date);
						}

						return {
							...notification_response,
							created_at: notification_response.created_at,
							seen_at: notification_response.seen_at ? notification_response.seen_at : null,
							clicked_at: notification_response.clicked_at ? notification_response.clicked_at : null,
							expires_at: notification_response.expires_at ? notification_response.expires_at : null,
							parsed_data: parsedData,
							isSeen: notification_response.isSeen ?? notification_response.seen_at !== null,
							isClicked: notification_response.isClicked ?? notification_response.clicked_at !== null,
						};
					} catch (e) {
						const errorDetails = {
							user_id: userInfo.id,
							notification_id: notification_response.id,
							notification_type: notification_response.notification_type,
							error_message: (e as Error).message,
							raw_data: notification_response.notification_data,
							data_type: typeof notification_response.notification_data,
						};

						const logLevel = e instanceof LeveledError ? e.level : "error";
						if (logLevel === "error") {
							trackErrorEvent("notifications_parse_failed", e, errorDetails);
						}
						axiosInstance
							.post(
								"/fe-logs",
								{ ...errorDetails, name: "notifications parsing failed" },
								{ params: { department_id: chosenDepartment.department_id, level: logLevel } },
							)
							.catch(console.error);

						return {
							...notification_response,
							created_at: notification_response.created_at,
							seen_at: notification_response.seen_at ? notification_response.seen_at : null,
							clicked_at: notification_response.clicked_at ? notification_response.clicked_at : null,
							expires_at: notification_response.expires_at ? notification_response.expires_at : null,
							parsed_data: null, // Set to null instead of undefined
							isSeen: notification_response.isSeen ?? notification_response.seen_at !== null,
							isClicked: notification_response.isClicked ?? notification_response.clicked_at !== null,
						};
					}
				})
				.filter((n): n is NotificationResponse => !!n);
			const notificationsArr = isAnyDemo ? injectBirthdayNotification(rawNotificationsArr) : rawNotificationsArr;
			setFetchedNotifications(notificationsArr);

			// Count notifications by type
			const typeCountMap: Record<string, number> = {};
			notificationsArr.forEach((notification) => {
				if (!notification) return;
				const type = notification.notification_type;
				typeCountMap[type] = (typeCountMap[type] || 0) + 1;
			});
		} catch (error) {
			trackErrorEvent("notifications_fetch_failed", error, {
				user_id: userInfo.id,
				department_id: chosenDepartment.department_id,
				error_details: (error as Error).message,
			});
			showSnackbar(t("error.fetch.notifications"), "error");
		} finally {
			loadingRef.current = false;
			setIsLoading(false);
		}
	}, [userInfo?.id, chosenDepartment.department_id, getWeekIndexByDate, getDayIndexByDate, showSnackbar, t]);

	// Mark notifications as seen
	const markNotificationsSeen = async (notificationIds: number[]): Promise<boolean> => {
		if (!notificationIds.length) return true;

		try {
			const request: MarkNotificationsSeenRequest = {
				notification_ids: notificationIds,
			};

			// Track which types of notifications are being marked as seen
			const notificationTypesBeingMarked = notificationIds.map((id) => {
				const notification = allNotifications.find((n) => n.id === id);
				return notification?.notification_type || "unknown";
			});

			// Count notifications by type
			const typeCountMap: Record<string, number> = {};
			notificationTypesBeingMarked.forEach((type) => {
				typeCountMap[type] = (typeCountMap[type] || 0) + 1;
			});

			await axiosInstance.post("/staffing/mark_notifications_seen", request);

			// Update local state — the unseen count re-derives from it.
			setFetchedNotifications((prev) =>
				prev.map((notification) => (notificationIds.includes(notification.id) ? { ...notification, isSeen: true, seen_at: DateTime.now() } : notification)),
			);

			return true;
		} catch (error) {
			showSnackbar("Failed to mark notifications as seen", "error");
			return false;
		}
	};

	// Mark notification as clicked
	const markNotificationClicked = async (notificationId: number): Promise<boolean> => {
		try {
			const request: MarkNotificationClickedRequest = {
				notification_id: notificationId,
			};

			// Find the notification to get its type
			const notification = allNotifications.find((n) => n.id === notificationId);
			const notificationType = notification?.notification_type || "unknown";
			const notificationCreatedAt = notification?.created_at || null;

			// Calculate age of notification
			let notificationAge = null;
			if (notificationCreatedAt) {
				const now = DateTime.now();
				notificationAge = now.diff(notificationCreatedAt, "hour");
			}

			await axiosInstance.post("/staffing/mark_notification_clicked", request);

			// Update local state
			setFetchedNotifications((prev) =>
				prev.map((notification) => (notification.id === notificationId ? { ...notification, isClicked: true, clicked_at: DateTime.now() } : notification)),
			);

			trackEvent("notification_marked_clicked", {
				user_id: userInfo?.id,
				notification_id: notificationId,
				notification_type: notificationType,
				was_previously_seen: notification?.isSeen || false,
				notification_age_hours: notificationAge,
				timestamp: new Date().toISOString(),
				action_source: "api_call",
			});

			return true;
		} catch (error) {
			trackErrorEvent("mark_notification_clicked_failed", error, {
				user_id: userInfo?.id,
				notification_id: notificationId,
				error_details: (error as Error).message,
			});
			showSnackbar("Failed to mark notification as clicked", "error");
			return false;
		}
	};

	const fetchNotificationMounted = useRef(false);
	useEffect(() => {
		if (!fetchNotificationMounted.current) {
			if (!userInfo?.id || chosenDepartment.department_id === "") return;
			fetchNotificationMounted.current = true;
			fetchNotifications();
		}
	}, [userInfo?.id, chosenDepartment.department_id]);

	return (
		<NotificationsContext.Provider
			value={{
				allNotifications,
				unseenNotificationCount,
				markNotificationsSeen,
				markNotificationClicked,
				isLoading,
				fetchNotifications,
			}}
		>
			{children}
		</NotificationsContext.Provider>
	);
};

export const useNotificationsContext = () => {
	const context = useContext(NotificationsContext);

	if (!context) throw new Error("useNotifications must be used within a NotificationsProvider");

	return context;
};
